<script setup lang="ts">
import { getDolphinChatRoomOwnURL } from '@/config'
import * as papaparse from 'papaparse'
import FlatButton from '@/components/atoms/FlatButton.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import { useRouter, useRoute } from 'vue-router'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import { ref, watch, computed } from 'vue'
import DolphinRadioButton from '@/components/atoms/DolphinRadioButton.vue'
import type { Option } from '@/types/app'
import { useAlert } from '@/libs/alertPortal'
import {
  useLineActivationManagerPageQuery,
  useLineActivationManagerPageExportUsersLazyQuery,
  type LineActivationManagerPageExportUsersQuery,
  useUpdateLoginLinkRedirectUriMutation,
} from './SettingsPageLineActivationManagerView.generated'
import SystemLoginSetting from './SystemLoginSetting.vue'

const generationMethod: Option<boolean>[] = [
  {
    value: false,
    text: '自社の管理番号を利用する',
  },
  {
    value: true,
    text: 'オカネコの管理番号を利用する',
  },
]

const route = useRoute()
const router = useRouter()
const alert = useAlert()
const useOkanekoUserId = ref(route.name === 'lineActivationSettingOkanekoId')

watch(useOkanekoUserId, value => {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push(
    value
      ? {
          name: 'lineActivationSettingOkanekoId',
        }
      : {
          name: 'lineActivationSettingSelfGenerationId',
        }
  )
})

const first = 100

const { load, fetchMore } = useLineActivationManagerPageExportUsersLazyQuery({
  first,
})

const { result, refetch } = useLineActivationManagerPageQuery()

const lineActivationProviderLiffAppConfig = computed(
  () => result.value?.lineActivationProviderLiffAppConfig
)

watch(result, value => {
  if (!value?.lineActivationProviderLiffAppConfig) {
    window.alert(
      '本機能のご利用にはLINEログインチャンネルおよびLIFF IDの設定が必要です。'
    )

    // eslint-disable-next-line fp/no-mutating-methods
    router.push({
      name: 'settings',
    })
  }
})

const isFetching = ref(false)

type ExportUser =
  LineActivationManagerPageExportUsersQuery['okanentClientUsersForForeignUser']['edges'][0]['node']

const downloadUsers = async () => {
  isFetching.value = true

  const buffer = ref<ExportUser[]>([])
  const after = ref<string | null>(null)

  try {
    while (true) {
      await load(undefined)

      const res = await fetchMore({
        variables: {
          first,
          after: after.value,
        },
      })
      if (!res) {
        break
      }

      after.value =
        res.data.okanentClientUsersForForeignUser.pageInfo.endCursor ?? null
      buffer.value = [
        ...buffer.value,
        ...res.data.okanentClientUsersForForeignUser.edges.map(
          edge => edge.node
        ),
      ]

      if (!res.data.okanentClientUsersForForeignUser.pageInfo.hasNextPage) {
        break
      }
    }

    const payload = buffer.value.map(user => {
      return {
        オカネコユーザーID: user.id,
        お客様ID: user.foreignUserID,
        チャットURL: user.user.chatRoom?.id
          ? getDolphinChatRoomOwnURL(user.user.chatRoom.id)
          : '',
      }
    })

    const link = document.createElement('a')
    link.href = URL.createObjectURL(
      new Blob([papaparse.unparse(payload)], { type: 'text/csv' })
    )
    link.download = '連携済みユーザーリスト.csv'
    link.click()
  } finally {
    isFetching.value = false
  }
}

const updateLoginLinkRedirectUriMutation =
  useUpdateLoginLinkRedirectUriMutation()

const save = async (value: { url: string }) => {
  try {
    await updateLoginLinkRedirectUriMutation.mutate({
      input: {
        redirectURI: value.url,
      },
    })

    alert.success('保存しました')
    refetch()
  } catch (e) {
    console.log(e)
    alert.error('保存に失敗しました')
  }
}
</script>

<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>自社アカウント連携</template>
    </SettingsPagePanelHeader>
    <section
      v-if="!lineActivationProviderLiffAppConfig?.canEditRedirectURI"
      :class="$style.section"
    >
      <p :class="$style.title">連携用URLの発行</p>
      <ul :class="$style.options">
        <li
          :class="$style.options__item"
          :key="index"
          v-for="(option, index) in generationMethod"
        >
          <label :class="$style.option">
            <DolphinRadioButton
              name="generationMethod"
              v-model="useOkanekoUserId"
              :value="option.value"
            />
            <span :class="$style.option__text">
              {{ option.text }}
            </span>
          </label>
        </li>
      </ul>
      <router-view />
    </section>
    <section v-else :class="$style.section">
      <SystemLoginSetting
        :liffAppConfig="lineActivationProviderLiffAppConfig"
        @save="save"
      />
    </section>
    <section :class="$style.section">
      <p :class="$style.title">連携済みユーザーリスト</p>
      <FlatButton
        :class="$style.download"
        variant="primary"
        size="medium"
        :disabled="isFetching"
        @click="downloadUsers"
      >
        <template v-if="isFetching">
          <LoadingIndicator />
        </template>
        <template v-else>連携済みユーザーリスト取得</template>
      </FlatButton>
    </section>
  </SettingsPagePanel>
</template>

<style module lang="scss">
.title {
  font-size: 14px;
  font-weight: 700;
}

.options {
  display: flex;
  gap: 20px;
  align-items: center;
  align-content: center;
  flex-wrap: wrap;
}

.option {
  display: inline-flex;
  align-items: center;

  & > * {
    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.download {
  width: 182px;
}

.section {
  &:nth-child(n + 2) {
    margin-top: 24px;
  }

  & > *:nth-child(n + 2) {
    margin-top: 24px;
  }
}
</style>
